/**
 * TOTP enrolment and challenge (security.md §2.1).
 *
 * SMS IS NOT AN OPTION HERE, and its absence is a decision rather than an oversight. SIM-swap
 * attacks make SMS a weak second factor for an account that can send legally binding documents.
 * SMS exists in this product only in the signer step-up ladder, where it is evidence of delivery to
 * a channel the sender nominated — a different claim, and an honest one.
 *
 * ## This screen used to promise recovery codes, and there are none
 *
 * It rendered a list headed "You will not see these again … they are the only way back in if you
 * lose your device". Supabase issues no recovery codes and nothing in this repository redeems one —
 * `MfaChallenge` below has said so in its own comment the whole time. Wiring the screen up as it
 * stood would have meant inventing a list that no sign-in would ever accept, which is worse than
 * showing none: somebody stores it, relies on it on the day they are locked out, and discovers then
 * that it was decorative. It is the same class of defect as the three untrue login-page claims.
 *
 * So the section is gone and the acknowledgement that replaced it states the real position — losing
 * the device means asking an operator to remove the factor. The checkbox stays for the reason it
 * always existed: it is friction that buys a support case avoided. What it now asks somebody to
 * acknowledge is merely true.
 */
import { useState } from 'react'
import { Button, Callout, Card, Field } from '@flowza/ui'

export function MfaEnrolment({
  secret,
  otpauthUri,
  qrCode,
  onConfirm,
  busy = false,
  error = null,
}: {
  readonly secret: string
  readonly otpauthUri: string
  /** Supabase's own SVG as a data URI, or null when it did not send one. */
  readonly qrCode?: string | null
  readonly onConfirm: (code: string) => void
  readonly busy?: boolean
  readonly error?: string | null
}) {
  const [code, setCode] = useState('')
  const [saved, setSaved] = useState(false)

  return (
    <Card title="Set up two-factor authentication">
      <p className="text-text-muted mt-0">
        Use an authenticator app. We do not offer SMS codes for account sign-in — phone numbers can
        be taken over, and an account that can send binding documents deserves better than that.
      </p>

      <div className="mt-4">
        <p className="m-0 font-medium">1. Add this to your authenticator app</p>
        {/* Supabase's own SVG, rendered straight from the data URI it sent. No third-party QR
            service: that would post a live TOTP secret to someone else's server. The secret stays
            visible underneath, because a camera is not always an option. */}
        {typeof qrCode === 'string' && qrCode !== '' ? (
          <img
            src={qrCode}
            alt="QR code for your authenticator app"
            className="bg-surface-raised mt-2 h-44 w-44 rounded-md p-2"
          />
        ) : null}
        <p className="text-text-muted mb-0 mt-2 break-all font-mono text-sm">{secret}</p>
        <p className="visually-hidden">{otpauthUri}</p>
      </div>

      <div className="mt-6">
        <p className="m-0 font-medium">2. Know what happens if you lose the device</p>
        <Callout tone="warning" title="There are no recovery codes">
          This is the only second factor on your account. If you lose the device running your
          authenticator app, you cannot sign yourself back in — an operator has to remove the factor
          for you. Keep a second enrolled device, or make sure someone can reach you.
        </Callout>
        <label className="mt-3 flex items-center gap-2">
          <input
            type="checkbox"
            checked={saved}
            onChange={(e) => setSaved(e.currentTarget.checked)}
          />
          <span>I understand there is no self-service way back in</span>
        </label>
      </div>

      <form
        className="mt-6 flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault()
          onConfirm(code)
        }}
      >
        <p className="m-0 font-medium">3. Confirm with a code from the app</p>
        <Field
          label="6-digit code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.currentTarget.value.replace(/\D/g, ''))}
        />
        {error !== null ? (
          <Callout tone="danger" title="That code was not accepted">
            {error}
          </Callout>
        ) : null}
        <div>
          {/* Both gates: a confirmed code proves the app works, the checkbox proves they read the
              part that stops them locking themselves out. */}
          <Button type="submit" disabled={busy || !saved || code.length !== 6}>
            {busy ? 'Checking…' : 'Turn on two-factor authentication'}
          </Button>
        </div>
      </form>
    </Card>
  )
}

export function MfaChallenge({
  onSubmit,
  busy = false,
  error = null,
}: {
  readonly onSubmit: (code: string) => void
  readonly busy?: boolean
  readonly error?: string | null
}) {
  const [code, setCode] = useState('')

  return (
    <main className="mx-auto max-w-md px-4 py-12">
      <h1 className="m-0 text-2xl font-semibold tracking-tight">Two-factor authentication</h1>
      <p className="text-text-muted mb-6 mt-1">Enter the code from your authenticator app.</p>
      <Card>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            onSubmit(code)
          }}
        >
          <Field
            label="6-digit code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            autoFocus
            value={code}
            onChange={(e) => setCode(e.currentTarget.value.replace(/\D/g, ''))}
          />
          {error !== null ? (
            <Callout tone="danger" title="That code was not accepted">
              {error}
            </Callout>
          ) : null}
          <Button type="submit" disabled={busy || code.length !== 6}>
            {busy ? 'Checking…' : 'Continue'}
          </Button>
        </form>
        {/*
          Dead-end ban: losing a phone is the common case, not the exotic one.

          This line used to link to `/auth/recovery`, and that route has never existed — so the
          answer offered to somebody locked out of their own account was itself a dead end. `/auth/*`
          is a Pages rewrite to this app, so it would have answered 200 and rendered React Router's
          error page, which is invisible in every log. The same defect as the `/auth/reset` link
          (#172, #174), in the one place it costs the most.

          There is still no recovery route to point at, and the reason has narrowed to one thing.
          Enrolment exists now — `src/auth/mfa.ts` and Settings › Security — so the half of this
          comment that said the screens were "imported by nothing" is obsolete. What has NOT changed
          is that Supabase issues no recovery codes and nothing here redeems one, so there is no
          self-service way back in and this sentence names the door that IS open. When a redemption
          flow lands, this becomes a link again.
        */}
        <p className="text-text-muted mb-0 mt-4 text-sm">
          Lost your device? Email <a href="mailto:support@flowza.com">support@flowza.com</a> and we
          will verify you another way.
        </p>
      </Card>
    </main>
  )
}
